import { Flex } from '@mantine/core';
import React from 'react';
import { useParams } from 'react-router';
import CashflowContent from 'src/cashflows/CashflowContent';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useContentMeta } from 'src/UI/app-container';
import FileUploadPage, {
  FileUploadTabNames,
} from 'src/data-capture/file-management/FileUploadPage';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FinancialContent } from 'src/financial/financial-data/FinancialContent';
import DeprecatedNonFinancialDataContent from 'src/non-financial-data/DeprecatedNonFanancialDataContent';
import NonFinancialDataContent from 'src/non-financial-data/NonFanancialDataContent';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import ClientMeetingsContent from 'src/meetings/client/ClientMeetingsContent';

export const ClientTabs = {
  NonFinancialData: 'non-financial-data',
  FinancialData: 'financial-data',
  Cashflows: 'cashflows',
  Documents: 'document',
  Meetings: 'meetings',
} as const;
type TabName = (typeof ClientTabs)[keyof typeof ClientTabs];

export const CLIENT_TABS_REGEX = new RegExp(
  `/client/[^/]+/(${Object.values(ClientTabs).join('|')}).*`,
);

export default function ClientData() {
  const { clientId } = useCurrentClient();
  const { tab, sectionSlug } = useParams<{ tab: TabName; sectionSlug?: string }>();
  const { flagEnabledRemoveDynamicUi, flagEnabledNewLayout } = useFlags();

  useContentMeta({ pageType: 'client', entityId: clientId });

  const tabContent = getClientTabContent(tab, flagEnabledRemoveDynamicUi);

  const hideChrome =
    flagEnabledNewLayout && tab === ClientTabs.FinancialData && sectionSlug === 'create';

  return (
    <Flex direction="column" gap="md" pos={'relative'}>
      {!hideChrome && (
        <Flex direction="row" justify={'space-between'}>
          <UnderlineTabs
            mode="router"
            showClientSearch
            tabs={[
              {
                label: 'Profile',
                href: `/client/${clientId}/${ClientTabs.NonFinancialData}`,
              },
              {
                label: 'Financial',
                href: `/client/${clientId}/${ClientTabs.FinancialData}`,
              },
              {
                label: 'Cashflows',
                href: `/client/${clientId}/${ClientTabs.Cashflows}`,
              },
              {
                label: 'Documents',
                href: `/client/${clientId}/${ClientTabs.Documents}/${FileUploadTabNames.ToReview}`,
                matchHref: `/client/${clientId}/${ClientTabs.Documents}`,
              },
              {
                label: 'Meetings',
                href: `/client/${clientId}/${ClientTabs.Meetings}`,
              },
            ]}
          />
        </Flex>
      )}

      {tabContent}
    </Flex>
  );
}

function getClientTabContent(tab: TabName | undefined, flagEnabledRemoveDynamicUi: boolean) {
  if (tab === ClientTabs.NonFinancialData) {
    return flagEnabledRemoveDynamicUi ? (
      <NonFinancialDataContent />
    ) : (
      <DeprecatedNonFinancialDataContent />
    );
  }
  if (tab === ClientTabs.FinancialData) {
    return <FinancialContent />;
  }
  if (tab === ClientTabs.Cashflows) {
    return <CashflowContent />;
  }
  if (tab === ClientTabs.Documents) {
    return <FileUploadPage />;
  }
  if (tab === ClientTabs.Meetings) {
    return <ClientMeetingsContent />;
  }
  return null;
}
